import { liveQuery } from 'dexie'
import type { Context } from '../../types/content'
import { useDatabase } from '../composables/useDatabase'
import { CONTEXT_COLOR_NAMES } from '../constants/colors'
import { MAX_CONTEXTS } from '../constants/contexts'
import { tryCatch } from '../utils/result'

const LAST_SELECTED_CONTEXT_KEY = 'lastSelectedContext'

export const useContextsStore = defineStore('contexts', () => {
  const dbOps = useDatabase()

  type ContentType = 'notes' | 'tasks' | 'lists'

  const contexts = ref<Context[]>([])
  const currentContext = ref<Context>()
  const isInitialized = ref<boolean>(false)
  const isShowingContexts = ref<boolean>(false)
  const isSwitchingContexts = ref<boolean>(false)
  const pendingMove = ref<{ type: ContentType; ids: string[] } | null>(null)

  const contextObservable = liveQuery(async () => {
    return dbOps.getItems<Context>('contexts')
  })

  contextObservable.subscribe((newContexts) => {
    contexts.value = newContexts
  })

  const availableColors = computed(() => {
    const usedColors = new Set(
      contexts.value.map((context: Context) => context.color)
    )
    return CONTEXT_COLOR_NAMES.filter(
      (colorName: string) => !usedColors.has(colorName)
    )
  })

  function openContextSwitcher() {
    isSwitchingContexts.value = true
  }

  function openContextSwitcherForMove(type: ContentType, ids: string[]) {
    pendingMove.value = { type, ids }
    isSwitchingContexts.value = true
  }

  async function moveContentToContext(
    type: ContentType,
    ids: string[],
    targetContext: Context
  ) {
    const notesStore = useNotesStore()
    const tasksStore = useTasksStore()
    const listsStore = useListsStore()

    const moveActions: Record<
      ContentType,
      (ids: string[], contextId: string) => Promise<void>
    > = {
      notes: (ids, contextId) =>
        notesStore.moveNotesToAnotherContext(ids, contextId),
      tasks: (ids, contextId) =>
        tasksStore.moveTasksToAnotherContext(ids, contextId),
      lists: (ids, contextId) =>
        listsStore.moveListsToAnotherContext(ids, contextId)
    }

    await moveActions[type](ids, targetContext.id)
  }

  function clearPendingMove() {
    pendingMove.value = null
    isSwitchingContexts.value = false
  }

  /**
   * Returns the next available color from the CONTEXT_COLOR_NAMES array.
   * If there are available colors, it returns the first one, otherwise it returns the first color from the array.
   *
   * @returns The next available color as a string.
   */
  function getNextAvailableColor(): string {
    return (availableColors.value[0] as string) ?? CONTEXT_COLOR_NAMES[0]
  }

  /**
   * Opens the context selector.
   *
   * This function sets the isShowingContexts flag to true, which shows the context selector.
   */
  function openContextSelector() {
    isShowingContexts.value = true
  }

  /**
   * Initializes the contexts store.
   *
   * This function loads the contexts from the database and sets the last used context as current context.
   * If there are no contexts in the database, it creates the default contexts and sets the first one as current context.
   *
   * @returns A void promise that resolves when the contexts are initialized.
   */
  async function initializeContexts(): Promise<void> {
    // Only consider active (non-deleted) contexts. Soft-deleted contexts
    // must not prevent the creation of defaults on a fresh device.
    const activeContexts = await dbOps.getItems<Context>('contexts')
    contexts.value = activeContexts.sort((a, b) => a.position - b.position)

    if (contexts.value.length > 0) {
      const lastContextId = getLastcontextId()
      const selectedContextId = lastContextId ?? contexts.value[0]?.id
      currentContext.value = contexts.value.find(
        (context: Context) => context.id === selectedContextId
      )
    } else {
      currentContext.value = undefined
    }

    isInitialized.value = true
  }

  /**
   * Stores the given context in the local storage as the last used context.
   *
   * @param context The context to store as the last used context.
   */
  function storeLastcontextId(context: Context) {
    localStorage.setItem(LAST_SELECTED_CONTEXT_KEY, context.id)
  }

  /**
   * Returns the last used context ID from local storage, or null if it doesn't exist.
   *
   * @returns The last used context ID, or null if it doesn't exist.
   */
  function getLastcontextId(): string | null {
    const lastSelectedContext = localStorage.getItem(LAST_SELECTED_CONTEXT_KEY)
    return lastSelectedContext ?? null
  }

  function getNextAvailablePosition(): number {
    return contexts.value.length
  }

  /**
   * Checks if there are contexts in the database.
   *
   * @returns A promise that resolves with a boolean indicating whether there are contexts in the database.
   */
  async function checkIfTheresContexts(): Promise<boolean> {
    const loadedContexts = await dbOps.get<Context>('contexts')

    return loadedContexts.length > 0
  }

  /**
   * Add a new context.
   *
   * This function creates a new context with a UUID as id, the translated name
   * "New context" and the next available color from the DEFAULT_COLORS array.
   * Then, it adds the new context to the contexts array and to the database.
   */
  async function addNewContext(name: string): Promise<Context | null> {
    if (contexts.value.length >= MAX_CONTEXTS) {
      console.warn('Max number of contexts reached')
      return null
    }

    const context = {
      position: getNextAvailablePosition(),
      name: name,
      color: getNextAvailableColor()
    }

    const result = await tryCatch(() =>
      dbOps.addItem('contexts', context as Context)
    )
    if (!result.ok) {
      console.error('Failed to add context:', result.error)
      return null
    }
    return result.value as Context
  }

  /**
   * Updates a context.
   *
   * This function updates a context with the given `id` with the given properties.
   * It updates the context in the database and in the `contexts` array if the context
   * is found in the array. If the update fails, the function returns early without
   * modifying the state.
   *
   * @param context The context to update. Must have an `id` property.
   */
  async function updateContext(context: Partial<Context & { id: string }>) {
    if (!context.id) return
    const updates = { ...context }

    if (updates.name) {
      updates.name = updates.name.slice(0, 32)
    }

    const result = await tryCatch(() => dbOps.updateItem('contexts', updates))
    if (!result.ok) {
      console.error('Failed to update context:', result.error)
      return
    }
    if (!result.value) return

    const index = contexts.value.findIndex((c) => c.id === context.id)
    if (index !== -1 && contexts.value[index] !== undefined) {
      contexts.value[index] = { ...contexts.value[index], ...result.value }
    }

    if (currentContext.value?.id === context.id) {
      currentContext.value = result.value as Context
    }
  }

  /**
   * Removes a context.
   *
   * If the context to be removed is the only active context, this function does
   * nothing. Otherwise, it marks the context as deleted by setting its `deletedAt`
   * property to the current timestamp. If the current context is the one being
   * removed, it sets the current context to the first active context that is not
   * being removed.
   * @param id The ID of the context to remove.
   */
  async function removeContext(id: string) {
    if (contexts.value.length === 1) {
      return
    }

    // Cascade soft-delete all content belonging to this context so that
    // the deletions propagate to remote on next sync and other devices
    // download them. This prevents orphaned content from reappearing
    // in a new context that reuses the same slot.
    const now = Date.now()
    const { db } = useDatabase()
    await db.notes
      .where('contextId')
      .equals(id)
      .modify({ deletedAt: now, updatedAt: now })
    await db.tasks
      .where('contextId')
      .equals(id)
      .modify({ deletedAt: now, updatedAt: now })
    await db.lists
      .where('contextId')
      .equals(id)
      .modify({ deletedAt: now, updatedAt: now })
    await db.labels
      .where('contextId')
      .equals(id)
      .modify({ deletedAt: now, updatedAt: now })
    await db.entries
      .where('contextId')
      .equals(id)
      .modify({ deletedAt: now, updatedAt: now })

    await updateContext({ id: id, deletedAt: Date.now() })

    if (currentContext.value?.id === id) {
      const activeContexts = contexts.value.filter(
        (c) => c.deletedAt === null && c.id !== id
      )
      setCurrentContext(activeContexts[0] as Context)
    }
  }

  /**
   * Restores a soft deleted context by its ID.
   *
   * This function sets the `deletedAt` property of the context with the given ID to null.
   * @param id The ID of the context to restore.
   */
  async function restoreContext(id: string) {
    await updateContext({ id: id, deletedAt: null })
  }

  /**
   * Sets the current context to the given context.
   *
   * This function sets the `currentContext` to the given context and saves the
   * context ID to the database in the `lastSelectedContext` metadata.
   *
   * @param context The context to set as the current context.
   */
  async function setCurrentContext(context: Context) {
    currentContext.value = context
    localStorage.setItem(LAST_SELECTED_CONTEXT_KEY, context.id)
  }

  /**
   * Updates the order of contexts.
   *
   * This function takes an array of Context objects, maps over it to update the
   * position property of each context, and then updates the contexts in the
   * database in bulk.
   *
   * @param {Context[]} items The array of Context objects to update.
   */
  async function updateContextOrder(items: Context[]) {
    const updatedContexts = items.map((context, index) => ({
      ...context,
      position: index
    }))

    const result = await tryCatch(() =>
      dbOps.updateInBulk('contexts', updatedContexts)
    )
    if (!result.ok) {
      console.error('Failed to update context order:', result.error)
    }
  }

  return {
    contexts,
    currentContext,
    availableColors,
    isInitialized,
    isShowingContexts,
    isSwitchingContexts,
    pendingMove,
    initializeContexts,
    openContextSelector,
    openContextSwitcher,
    openContextSwitcherForMove,
    moveContentToContext,
    clearPendingMove,
    checkIfTheresContexts,
    addNewContext,
    updateContext,
    removeContext,
    restoreContext,
    setCurrentContext,
    updateContextOrder
  }
})
